import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const WHATSAPP_NUMBER = '918880331224';

const slides = [
  {
    id: 1,
    category: 'Construction Projects',
    heading: 'Building Exceptional Residential & Commercial Spaces',
    description: 'From dream homes to large commercial buildings, we deliver high-quality construction with precision engineering, transparent processes, and reliable timelines.',
    image: 'https://customer-assets.emergentagent.com/job_04b87573-a1ae-4a6c-8a55-99387c57d38e/artifacts/d0btcsnl_depositphotos_327986174-stock-photo-two-storey-houses-construction-thailand.jpg',
    link: '/projects',
  },
  {
    id: 2,
    category: 'Interior Design',
    heading: 'Transforming Spaces with Elegant Interiors',
    description: 'We design stylish, functional interiors tailored to your lifestyle and business needs, blending aesthetics with comfort and efficiency.',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1920&q=80',
    link: '/interiors',
  },
  {
    id: 3,
    category: 'Building Materials',
    heading: 'Premium Construction Materials You Can Trust',
    description: 'We supply reliable, high-quality construction materials sourced from trusted manufacturers to ensure durability and strength in every project.',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1920&q=80',
    link: '/materials',
  },
  {
    id: 4,
    category: 'Layouts & Developments',
    heading: 'Smart Layout Planning & Property Development',
    description: 'From land planning to large layout developments, we create well-structured, future-ready communities designed for growth and value.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920&q=80',
    link: '/services',
  },
  {
    id: 5,
    category: 'Real Estate',
    heading: 'Find the Perfect Property Investment',
    description: 'We help clients buy and sell residential and commercial properties with expert guidance, transparency, and the best market opportunities.',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1920&q=80',
    link: '/contact',
  },
];

export default function HeroCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const swiperRef = useRef(null);

  const handleConsultationClick = () => {
    const message = encodeURIComponent('Hello! I would like to get a free consultation for my construction/interior project.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <section 
      className="relative w-full h-screen min-h-[700px] max-h-[900px] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="hero-carousel"
    >
      {/* Architectural Line Graphics Background */}
      <div className="absolute inset-0 z-0 opacity-[0.08] pointer-events-none">
        <svg className="w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
          {/* Grid Lines */}
          {[...Array(20)].map((_, i) => (
            <line key={`v-${i}`} x1={i * 100} y1="0" x2={i * 100} y2="1080" stroke="#8B4513" strokeWidth="0.5" />
          ))}
          {[...Array(12)].map((_, i) => (
            <line key={`h-${i}`} x1="0" y1={i * 100} x2="1920" y2={i * 100} stroke="#8B4513" strokeWidth="0.5" />
          ))}
          {/* Decorative Architecture Elements */}
          <path d="M1600 200 L1700 100 L1800 200" fill="none" stroke="#8B4513" strokeWidth="1" />
          <circle cx="1700" cy="150" r="30" fill="none" stroke="#8B4513" strokeWidth="1" />
          <rect x="100" y="800" width="200" height="200" fill="none" stroke="#8B4513" strokeWidth="1" />
          <path d="M200 800 L200 700 Q300 650 400 700 L400 800" fill="none" stroke="#8B4513" strokeWidth="1" />
        </svg>
      </div>

      {/* Main Swiper */}
      <Swiper
        ref={swiperRef}
        modules={[Autoplay, EffectFade, Navigation, Pagination]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={1000}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        pagination={{
          clickable: true,
          renderBullet: (index, className) => {
            return `<span class="${className} hero-pagination-bullet"></span>`;
          },
        }}
        navigation={{
          prevEl: '.hero-prev',
          nextEl: '.hero-next',
        }}
        loop={true}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className="w-full h-full"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <div className="relative w-full h-full flex">
              {/* Left Content Section - 40-45% */}
              <div className="relative z-20 w-full lg:w-[45%] h-full flex flex-col justify-center px-6 md:px-12 lg:px-16 xl:px-24 bg-[#ececec] lg:bg-[#ececec]">
                
                <AnimatePresence mode="wait">
                  {activeIndex === index && (
                    <motion.div
                      key={slide.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -30 }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                      className="relative z-10"
                    >
                      {/* Category Tag */}
                      <motion.span
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="inline-block text-[#C9A646] text-xs uppercase tracking-[0.25em] font-bold mb-4 md:mb-6"
                      >
                        {slide.category}
                      </motion.span>

                      {/* Heading */}
                      <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl text-gray-900 leading-tight mb-4 md:mb-6"
                      >
                        {slide.heading.split(' ').map((word, i, arr) => (
                          <span key={i}>
                            {i >= arr.length - 2 ? (
                              <span className="text-[#8B4513]">{word} </span>
                            ) : (
                              <span>{word} </span>
                            )}
                          </span>
                        ))}
                      </motion.h1>

                      {/* Description */}
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-gray-600 text-sm md:text-base lg:text-lg leading-relaxed mb-6 md:mb-8 max-w-xl"
                      >
                        {slide.description}
                      </motion.p>

                      {/* CTA Buttons */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="flex flex-col sm:flex-row gap-3 md:gap-4"
                      >
                        <button
                          onClick={handleConsultationClick}
                          className="btn-primary inline-flex items-center justify-center gap-2 text-sm md:text-base"
                          data-testid="hero-consultation-btn"
                        >
                          Book Free Consultation
                          <ArrowRight size={18} />
                        </button>
                        <Link
                          to={slide.link}
                          className="btn-secondary inline-flex items-center justify-center gap-2 text-sm md:text-base"
                          data-testid="hero-projects-btn"
                        >
                          View Projects
                          <ArrowRight size={18} />
                        </Link>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Right Image Section - 55-60% */}
              <div className="absolute lg:relative right-0 top-0 w-full lg:w-[55%] h-full">
                {/* Image with professional left fade */}
                <div className="absolute inset-0">
                  <img
                    src={slide.image}
                    alt={slide.heading}
                    className="w-full h-full object-cover"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                  {/* White gradient fade on left edge for smooth transition */}
                  <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#ececec] to-transparent" />
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Navigation Arrows */}
      <div className="absolute bottom-8 right-8 z-30 hidden md:flex items-center gap-3">
        <button
          className="hero-prev w-12 h-12 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 flex items-center justify-center text-gray-900 hover:bg-[#C9A646] hover:text-[#0A0A0A] hover:border-[#C9A646] transition-all duration-300 group"
          data-testid="hero-prev-btn"
        >
          <ChevronLeft size={24} className="group-hover:scale-110 transition-transform" />
        </button>
        <button
          className="hero-next w-12 h-12 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 flex items-center justify-center text-gray-900 hover:bg-[#C9A646] hover:text-[#0A0A0A] hover:border-[#C9A646] transition-all duration-300 group"
          data-testid="hero-next-btn"
        >
          <ChevronRight size={24} className="group-hover:scale-110 transition-transform" />
        </button>
      </div>

      {/* Slide Counter */}
      <div className="absolute bottom-8 left-8 z-30 hidden md:flex items-center gap-4">
        <span className="font-heading text-4xl text-[#C9A646]">
          {String(activeIndex + 1).padStart(2, '0')}
        </span>
        <div className="w-16 h-[2px] bg-gray-100">
          <motion.div
            className="h-full bg-[#C9A646]"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 5, ease: 'linear' }}
            key={activeIndex}
          />
        </div>
        <span className="text-gray-900/40 text-sm">
          {String(slides.length).padStart(2, '0')}
        </span>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 hidden lg:flex flex-col items-center gap-2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span className="text-gray-900/40 text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-[1px] h-8 bg-gradient-to-b from-[#C9A646] to-white/0" />
      </motion.div>
    </section>
  );
}
